
import React, { useState, useEffect } from "react";
import { Play, SkipBack, SkipForward, Hotel, Building, Home } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import UseCasePlayer from "./UseCasePlayer";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";

const useCaseData = [
  {
    id: "luxury-hotel",
    title: "Luxury Hotel Chain",
    location: "Miami, USA",
    flag: "🇺🇸",
    icon: <Building className="h-5 w-5" />,
    avatar: "/lovable-uploads/4d422b74-a804-4162-83bb-487c58649bfb.png",
    description: "Experience a game-changer in the hospitality industry, as our AI assistant handles guest inquiries about hotel amenities and local recommendations.",
    details: [
      { label: "Industry", value: "Luxury Hotels" },
      { label: "Language", value: "English (USA)" },
      { label: "Function", value: "Guest Services" }
    ],
    audioSrc: "/HostCall-ai-welcome-2025-05.mp3",
    duration: "4:00"
  },
  {
    id: "vacation-rental",
    title: "Vacation Rental Network",
    location: "Barcelona, Spain",
    flag: "🇪🇸",
    icon: <Home className="h-5 w-5" />,
    avatar: "/lovable-uploads/4d422b74-a804-4162-83bb-487c58649bfb.png",
    description: "Experience how our AI assistant helped a vacation rental company automate 78% of guest check-in instructions and property information.",
    details: [
      { label: "Industry", value: "Vacation Rentals" },
      { label: "Language", value: "Spanish & English" },
      { label: "Function", value: "Check-in Automation" }
    ],
    audioSrc: "/HostCall-ai-welcome-2025-05.mp3",
    duration: "4:00"
  },
  {
    id: "boutique-hotel",
    title: "Boutique Hotel",
    location: "Paris, France",
    flag: "🇫🇷",
    icon: <Hotel className="h-5 w-5" />,
    avatar: "/lovable-uploads/4d422b74-a804-4162-83bb-487c58649bfb.png",
    description: "Discover how a 24-room boutique hotel provides personalized concierge services to every guest with our AI voice assistant.",
    details: [
      { label: "Industry", value: "Boutique Hospitality" },
      { label: "Language", value: "French & English" },
      { label: "Function", value: "Concierge Services" }
    ],
    audioSrc: "/HostCall-ai-welcome-2025-05.mp3",
    duration: "4:00"
  },
  {
    id: "airbnb-host",
    title: "Independent Airbnb Host",
    location: "Tokyo, Japan",
    flag: "🇯🇵",
    icon: <Home className="h-5 w-5" />,
    avatar: "/lovable-uploads/4d422b74-a804-4162-83bb-487c58649bfb.png",
    description: "See how a single host managing 5 properties uses AI to provide 24/7 guest support in multiple languages.",
    details: [
      { label: "Industry", value: "Short-Term Rental" },
      { label: "Language", value: "Japanese & English" },
      { label: "Function", value: "Guest Communication" }
    ],
    audioSrc: "/HostCall-ai-welcome-2025-05.mp3",
    duration: "4:00"
  }
];

const UseCaseShowcase: React.FC = () => {
  const [activeUseCase, setActiveUseCase] = useState(useCaseData[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();
  
  // Stop audio playback when changing use cases
  const handleSelectUseCase = (useCase) => {
    setIsPlaying(false);
    setActiveUseCase(useCase);
  };

  const handlePlayToggle = () => {
    setIsPlaying(!isPlaying);
  };

  const handleNext = () => {
    const currentIndex = useCaseData.findIndex(uc => uc.id === activeUseCase.id);
    const nextIndex = (currentIndex + 1) % useCaseData.length;
    setActiveUseCase(useCaseData[nextIndex]);
    setIsPlaying(false);
  };

  const handlePrevious = () => {
    const currentIndex = useCaseData.findIndex(uc => uc.id === activeUseCase.id);
    const prevIndex = (currentIndex - 1 + useCaseData.length) % useCaseData.length;
    setActiveUseCase(useCaseData[prevIndex]);
    setIsPlaying(false);
  };
  
  // Attempt to unlock audio on component mount
  useEffect(() => {
    // Create a silent audio context to unlock audio on iOS
    const unlockAudio = () => {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = audioContext.createBufferSource();
      source.buffer = audioContext.createBuffer(1, 1, 22050);
      source.connect(audioContext.destination);
      source.start(0);
      
      // Clean up
      setTimeout(() => {
        if (audioContext.state === "running") {
          audioContext.close();
        }
      }, 1000);
    };
    
    // Unlock audio on first interaction
    const handleInteraction = () => {
      unlockAudio();
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
    
    document.addEventListener('click', handleInteraction);
    document.addEventListener('touchstart', handleInteraction);
    document.addEventListener('keydown', handleInteraction);
    
    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, []);

  return (
    <section className="py-16 bg-gradient-to-b from-brand-900 to-brand-950">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4 animate-fade-in">
            {t("useCaseShowcase.title")}
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto animate-fade-in delay-300">
            {t("useCaseShowcase.subtitle")}
          </p>
        </div>

        <div className="grid lg:grid-cols-7 gap-8 max-w-5xl mx-auto">
          {/* Main Player - Left Side */}
          <div className="lg:col-span-4">
            <UseCasePlayer 
              useCase={activeUseCase} 
              isPlaying={isPlaying} 
              onPlayToggle={handlePlayToggle}
              onNext={handleNext}
              onPrevious={handlePrevious}
            />
          </div>

          {/* Use Case List - Right Side */}
          <div className="lg:col-span-3 bg-gray-900/50 rounded-3xl p-2 backdrop-blur-sm">
            <div className="flex items-center justify-between px-4 py-2 mb-1">
              <h3 className="text-white font-medium">Select Use Case</h3>
              <span className="text-gray-400 text-sm">Duration</span>
            </div>
            
            {/* List of use cases */}
            <div className="space-y-1 max-h-[400px] overflow-y-auto no-scrollbar">
              {useCaseData.map((useCase) => (
                <button
                  key={useCase.id}
                  className={`w-full text-left p-3 rounded-xl flex items-center justify-between gap-3 transition-colors
                    ${activeUseCase.id === useCase.id 
                      ? 'bg-purple-600 text-white' 
                      : 'hover:bg-white/5 text-white'}`}
                  onClick={() => handleSelectUseCase(useCase)}
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 border border-gray-700">
                      <AvatarImage src={useCase.avatar} alt={useCase.title} className="object-cover" />
                      <AvatarFallback className="bg-brand-800 text-white">
                        {useCase.icon}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium flex items-center gap-1">
                        {useCase.title} <span className="text-sm">{useCase.flag}</span>
                      </p>
                      <p className={`text-xs ${activeUseCase.id === useCase.id ? 'text-white/95' : 'text-gray-400'}`}>{useCase.location}</p>
                    </div>
                  </div>
                  <span className={`text-sm ${activeUseCase.id === useCase.id ? 'text-white/95' : 'text-gray-400'}`}>{useCase.duration}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="text-center mt-12">
          <p className="text-gray-300 mb-6">
            Want to see how our AI can transform your hospitality business?
          </p>
          <Button 
            className="px-8 py-6 bg-brand-600 hover:bg-brand-700 text-white rounded-md font-medium"
            onClick={() => window.location.href = "/demo#demo-request-form"}
          >
            Schedule a Custom Demo
          </Button>
        </div>
      </div>
    </section>
  );
};

export default UseCaseShowcase;
